// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * What kind of change a maintenance record is, and what was done (design doc §8.6).
 *
 * The kind is chosen from a fixed list — the server's `ChangeType` — so records can be
 * counted by it; the description beside it is free text, optional except for 其他, which
 * has nothing else to go on. Records from before the split are 其他, their old reason the
 * description.
 */

import { useTranslation } from "react-i18next";

export const CHANGE_TYPES = [
  "REPAIR",
  "PREVENTIVE",
  "UPGRADE",
  "ASSEMBLY",
  "QUALITY",
  "OTHER",
] as const;

export type ChangeType = (typeof CHANGE_TYPES)[number];

export function needsDescription(changeType: ChangeType | ""): boolean {
  return changeType === "OTHER";
}

/** The dropdown on the change forms. Starts on 请选择: the kind is a choice, not a default. */
export function ChangeTypeSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: ChangeType | "";
  onChange: (value: ChangeType | "") => void;
}) {
  const { t } = useTranslation();
  return (
    <select
      id={id}
      required
      value={value}
      onChange={(event) => onChange(event.target.value as ChangeType | "")}
    >
      <option value="">{t("maintenance.changeTypeChoose")}</option>
      {CHANGE_TYPES.map((type) => (
        <option key={type} value={type}>
          {t(`maintenance.changeTypes.${type}`)}
        </option>
      ))}
    </select>
  );
}

/** A record's kind and description, where a list shows it: the kind as a tag, then what
 *  was done. */
export function ChangeSummary({
  changeType,
  reason,
}: {
  changeType: string | null | undefined;
  reason: string | null | undefined;
}) {
  const { t } = useTranslation();
  return (
    <>
      {changeType && (
        <span className="badge badge-change-type">
          {t(`maintenance.changeTypes.${changeType}`)}
        </span>
      )}
      {reason && <span className="change-description">{reason}</span>}
    </>
  );
}

/** The same, as one line of text — for a sentence such as the revoke confirmation. */
export function changeText(
  t: (key: string) => string,
  changeType: string | null | undefined,
  reason: string | null | undefined,
): string {
  const label = changeType ? t(`maintenance.changeTypes.${changeType}`) : "";
  return [label, reason].filter(Boolean).join(" · ");
}
